/**
 * Custom hook for tracking deletion state via signals
 * Updates the deletion state manager when deletion signals are received
 */
import { useEffect } from 'react';
import { useSignal } from '@backstage/plugin-signals-react';
import { EventType } from '@devxdiscover/devhub-common';
import { deletionStateManager } from '../../deletionStateManager';

interface DeletionSignal {
  [key: string]: any;
  event: EventType;
  taskId?: string;
  applicationId?: number;
  status?: 'pending' | 'in-progress' | 'completed' | 'failed' | 'skipped';
  error?: string;
  success?: boolean;
}

/**
 * Hook for monitoring deletion signals and updating deletion state
 * @param applicationId - Optional application ID to filter signals
 */
export const useDeletionSignalTracking = (applicationId?: number) => {
  const { lastSignal } = useSignal<DeletionSignal>('devhub-channel');

  useEffect(() => {
    if (!lastSignal) return;

    // eslint-disable-next-line no-console
    console.log('[useDeletionSignalTracking] RAW signal received:', {
      signal: lastSignal,
      filteringBy: applicationId,
      signalAppId: lastSignal.applicationId,
      dataAppId: (lastSignal as any).data?.applicationId,
    });

    // Filter by applicationId if provided
    // Check both top-level and nested in data
    const signalAppId =
      lastSignal.applicationId || (lastSignal as any).data?.applicationId;
    if (applicationId && signalAppId !== applicationId) {
      // eslint-disable-next-line no-console
      console.log(
        '[useDeletionSignalTracking] Signal filtered out - applicationId mismatch',
      );
      return;
    }

    // Normalize signal to have applicationId at top level
    const signal = {
      ...lastSignal,
      applicationId: signalAppId,
    };

    // eslint-disable-next-line no-console
    console.log('[useDeletionSignalTracking] Received signal:', {
      event: signal.event,
      applicationId: signal.applicationId,
      status: signal.status,
      error: signal.error,
      tracking: applicationId,
    });

    // Track failures for each resource type
    if (signal.status === 'failed' && signal.applicationId) {
      const resourceMap: Record<string, string> = {
        [EventType.GITHUB_REPO_DELETION]: 'github',
        [EventType.CNAP_TENANCY_DELETION]: 'cnap',
        [EventType.AD_GROUPS_DELETION]: 'adGroups',
      };

      // eslint-disable-next-line no-console
      console.log('[useDeletionSignalTracking] Resource map:', resourceMap);
      // eslint-disable-next-line no-console
      console.log('[useDeletionSignalTracking] Event type:', signal.event);

      const resourceType = resourceMap[signal.event];
      // eslint-disable-next-line no-console
      console.log(
        '[useDeletionSignalTracking] Mapped resource type:',
        resourceType,
      );

      if (resourceType) {
        const currentState = deletionStateManager.getDeletionState(
          signal.applicationId,
        );
        const existingFailures = currentState?.failedResources || [];
        const existingErrors = currentState?.errors || {};

        // Use error from signal if available, otherwise use a generic message
        const errorMessage =
          signal.error || `Failed to delete ${resourceType} resource`;

        // eslint-disable-next-line no-console
        console.log('[useDeletionSignalTracking] Error from signal:', {
          raw: signal.error,
          type: typeof signal.error,
          message: errorMessage,
        });

        // eslint-disable-next-line no-console
        console.log('[useDeletionSignalTracking] Marking as failed:', {
          applicationId: signal.applicationId,
          failedResources: [...existingFailures, resourceType],
          errors: { ...existingErrors, [resourceType]: errorMessage },
        });

        deletionStateManager.markAsFailed(
          signal.applicationId,
          [...existingFailures, resourceType],
          { ...existingErrors, [resourceType]: errorMessage },
        );

        // Force a re-render by triggering a storage event
        window.dispatchEvent(new Event('deletion-state-changed'));
      }
    }

    // Clear deletion state on successful application deletion
    if (
      signal.event === EventType.APPLICATION_DELETION &&
      signal.applicationId
    ) {
      // eslint-disable-next-line no-console
      console.log(
        '[useDeletionSignalTracking] APPLICATION_DELETION signal received:',
        {
          event: signal.event,
          applicationId: signal.applicationId,
          success: signal.success,
          dataSuccess: (signal as any).data?.success,
          status: signal.status,
          fullSignal: signal,
        },
      );

      // Check for success in multiple locations and status
      const isSuccess =
        signal.success ||
        (signal as any).data?.success ||
        signal.status === 'completed';

      if (isSuccess) {
        // eslint-disable-next-line no-console
        console.log(
          '[useDeletionSignalTracking] Deletion successful, clearing state for app:',
          signal.applicationId,
        );
        deletionStateManager.clearDeletion(signal.applicationId);
        // Force a re-render by triggering a storage event
        // This will trigger the cleanup in MyIdeasView which will refetch
        window.dispatchEvent(new Event('deletion-state-changed'));

        // Also dispatch a custom event to trigger a full refresh
        window.dispatchEvent(
          new CustomEvent('app-deleted', {
            detail: { applicationId: signal.applicationId },
          }),
        );
      }
    }
  }, [lastSignal, applicationId]);
};
